import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

export interface CustomsDeclarationItem {
  id?: string;
  line_number: number;
  goods_receipt_item_id: string;
  customs_value: string;
  duty_amount: string;
  excise_amount: string;
  vat_amount: string;
  vat_rate_id?: string | null;
  fee_allocated?: string;
  cost_addition?: string;
}

export interface CustomsDeclarationListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  customs_number: string;
  customs_date: string;
  total_customs_value: string;
  total_cost_addition: string;
  total_vat: string;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  // Block 5.3f:
  state?: CancellableState;
}

export interface CustomsDeclaration extends CustomsDeclarationListItem {
  country_origin_code: string | null;
  customs_fee: string;
  total_duty: string;
  total_excise: string;
  posted_at: string | null;
  comment: string | null;
  items: CustomsDeclarationItem[];
  // Block 5.3f:
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface CustomsDeclarationCreate {
  organization_id: string;
  document_number: string;
  document_date: string;
  customs_number: string;
  customs_date: string; // YYYY-MM-DD
  country_origin_code?: string | null;
  customs_fee?: string;
  comment?: string | null;
  items: CustomsDeclarationItem[];
}

export type CustomsDeclarationUpdate = Partial<CustomsDeclarationCreate>;

export const customsDeclarations = {
  list: (params?: {
    organization_id?: string; customs_number?: string;
    posted?: boolean; limit?: number; offset?: number;
  }) =>
    api.get<Page<CustomsDeclarationListItem>>('/customs-declarations', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<CustomsDeclaration>(`/customs-declarations/${id}`).then((r) => r.data),
  create: (payload: CustomsDeclarationCreate) =>
    api.post<CustomsDeclaration>('/customs-declarations', payload).then((r) => r.data),
  update: (id: string, payload: CustomsDeclarationUpdate) =>
    api.patch<CustomsDeclaration>(`/customs-declarations/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/customs-declarations/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<CustomsDeclaration>(`/customs-declarations/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<CustomsDeclaration>(`/customs-declarations/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/customs-declarations/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};
